/**
 * Proposal text sanitization utilities for safe ingestion into the Answer Library.
 */

function escapeRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Replaces occurrences of client name with [Client Name]
 */
export function sanitizeClient(text: string, clientName?: string): string {
  if (!text) return "";

  let result = text;

  if (clientName && clientName.trim()) {
    const trimmedClient = clientName.trim();
    const escaped = escapeRegex(trimmedClient);

    // Case-insensitive replacement
    const regex = new RegExp(`\\b${escaped}\\b`, "gi");
    result = result.replace(regex, "[Client Name]");

    // If client name had common prefix like "City of X" or "State of X",
    // also check if "X" alone appears if X is at least 4 letters
    const prefixMatch = trimmedClient.match(
      /^(?:city|state|county|department)\s+of\s+([A-Za-z\s]+)$/i,
    );
    if (prefixMatch && prefixMatch[1] && prefixMatch[1].trim().length >= 4) {
      const specificPart = prefixMatch[1].trim();
      const specificRegex = new RegExp(`\\b${escapeRegex(specificPart)}\\b`, "gi");
      result = result.replace(specificRegex, "[Client Name]");
    }
  }

  // Redact dollar figures / pricing (e.g. $50,000, $1.5M, $0.12)
  const dollarRegex = /\$\s*\d+(?:,\d{3})*(?:\.\d{1,4})?(?:\s*(?:million|billion|k|m))?\b/gi;
  result = result.replace(dollarRegex, "[Dollar Figure]");

  return result;
}

/**
 * Detects potential residual PII, contract numbers, or external emails in candidate text
 */
export function detectResidualWarnings(text: string): string[] {
  if (!text) return [];
  const warnings: string[] = [];

  // 1. External email address (ignoring mlconnections.com)
  const emailRegex = /\b[A-Za-z0-9._%+-]+@(?!mlconnections\.com)[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
  if (emailRegex.test(text)) {
    warnings.push("External email address detected");
  }

  // 2. Phone numbers (standard US formats)
  const phoneRegex = /\b(?:\+?1[-.]?)?\(?[0-9]{3}\)?[-.\s][0-9]{3}[-.\s][0-9]{4}\b/;
  if (phoneRegex.test(text)) {
    warnings.push("Direct phone number detected");
  }

  // 3. Specific RFP / Contract / Solicitation numbers
  const contractRegex =
    /\b(?:RFP|RFQ|IFB|Contract|Solicitation)\s*(?:#|No\.?|Number)?\s*[:#-]?\s*[A-Z0-9-]{4,}\b/i;
  if (contractRegex.test(text)) {
    warnings.push("Contract / Solicitation reference number detected");
  }

  // 4. Unresolved action items
  if (/\[ACTION NEEDED:[^\]]+\]/i.test(text)) {
    warnings.push("Unresolved [ACTION NEEDED] placeholder in text");
  }

  // 5. Specific dollar figures or pricing amounts
  const dollarRegex = /\$\s*\d+(?:,\d{3})*(?:\.\d{1,4})?(?:\s*(?:million|billion|k|m))?\b/i;
  if (dollarRegex.test(text)) {
    warnings.push("Dollar figure or pricing amount detected");
  }

  return warnings;
}

/**
 * Returns true if text contains any residual warnings
 */
export function hasResidualPii(text: string): boolean {
  return detectResidualWarnings(text).length > 0;
}
